import { resolveEventDisplayName } from '@/core/lib/lead-event-names';

type LeadDetailDict = {
  activity: string;
  noActivity: string;
  activityCreated: string;
  activityStatusChanged: string;
  activityDescriptionUpdated: string;
  activityFieldUpdated: string;
  activityFieldSet: string;
  activityFieldCleared: string;
  activityFieldNames: Record<
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'companyName'
    | 'jobTitle'
    | 'address',
    string
  >;
  activityTagAdded: string;
  activityTagRemoved: string;
  activityEventAdded: string;
  activityEventRemoved: string;
  activityEventRemovedBecauseDeleted: string;
  activityShowMore: string;
  activityShowLess: string;
  commentPlaceholder: string;
  commentSubmit: string;
  commentSubmitAria: string;
  activityAssigneeChanged: string;
  activityAssigneeChangedAndShared?: string;
  activitySharingChanged: string;
  activitySharingVisible: string;
  activitySharingHidden: string;
  activityAttachmentAdded: string;
  activityAttachmentRemoved: string;
  activityContactedViaCall: string;
  activityContactedViaEmail: string;
  activityContactedViaSms: string;
  activityContactedVia: string;
  activityArchived: string;
  activityUnarchived: string;
  assigneePicker?: { nobody?: string };
  cancel?: string;
  attachments?: {
    title?: string;
    empty?: string;
    add?: string;
    uploading?: string;
    businessCard?: string;
    image?: string;
    document?: string;
    uploadFailed?: string;
    removeFailed?: string;
    removed?: string;
    added?: string;
    fileTooLarge?: string;
    fileTypeNotAllowed?: string;
    open?: string;
    remove?: string;
    removeConfirmTitle?: string;
    removeConfirmDescriptionPrefix?: string;
    removeConfirmDescriptionSuffix?: string;
    removeConfirmDescriptionFallback?: string;
    confirmRemove?: string;
    reorder?: string;
    reorderFailed?: string;
  };
};

type LeadCreateDict = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workspace: string;
  jobTitle: string;
  address: string;
};

export function buildLeadDetailTimelineLabels(options: {
  detailDict: LeadDetailDict;
  eventNameById: Map<string, string>;
}) {
  const { detailDict, eventNameById } = options;
  return {
    title: detailDict.activity,
    empty: detailDict.noActivity,
    created: detailDict.activityCreated,
    statusChanged: detailDict.activityStatusChanged,
    descriptionUpdated: detailDict.activityDescriptionUpdated,
    fieldUpdated: detailDict.activityFieldUpdated,
    fieldSet: detailDict.activityFieldSet,
    fieldCleared: detailDict.activityFieldCleared,
    tagAdded: detailDict.activityTagAdded,
    tagRemoved: detailDict.activityTagRemoved,
    eventAdded: detailDict.activityEventAdded,
    eventRemoved: detailDict.activityEventRemoved,
    eventRemovedBecauseDeleted: detailDict.activityEventRemovedBecauseDeleted,
    resolveEventName: (
      eventId: string | null | undefined,
      storedName?: string | null,
    ) => {
      if (!eventId && !storedName) return '—';
      return resolveEventDisplayName(eventId, eventNameById, { storedName });
    },
    showMore: detailDict.activityShowMore,
    showLess: detailDict.activityShowLess,
    commentPlaceholder: detailDict.commentPlaceholder,
    commentSubmit: detailDict.commentSubmit,
    commentSubmitAria: detailDict.commentSubmitAria,
    fieldNames: detailDict.activityFieldNames,
    assigneeChanged: detailDict.activityAssigneeChanged,
    assigneeChangedAndShared:
      detailDict.activityAssigneeChangedAndShared ??
      detailDict.activityAssigneeChanged,
    nobody: detailDict.assigneePicker?.nobody ?? '—',
    sharingChanged: detailDict.activitySharingChanged,
    sharingVisible: detailDict.activitySharingVisible,
    sharingHidden: detailDict.activitySharingHidden,
    attachmentAdded: detailDict.activityAttachmentAdded,
    attachmentRemoved: detailDict.activityAttachmentRemoved,
    contactedViaCall: detailDict.activityContactedViaCall,
    contactedViaEmail: detailDict.activityContactedViaEmail,
    contactedViaSms: detailDict.activityContactedViaSms,
    contactedVia: detailDict.activityContactedVia,
    archived: detailDict.activityArchived,
    unarchived: detailDict.activityUnarchived,
  };
}

export function buildLeadDetailAttachmentLabels(options: {
  detailDict: LeadDetailDict;
  locale: string;
}) {
  const { detailDict, locale } = options;
  const attachmentsDict = detailDict.attachments;
  const isDe = locale === 'de';
  return {
    title: attachmentsDict?.title ?? (isDe ? 'Anhänge' : 'Attachments'),
    empty:
      attachmentsDict?.empty ??
      (isDe ? 'Noch keine Dateien angehängt.' : 'No files attached yet.'),
    add: attachmentsDict?.add ?? (isDe ? 'Datei hinzufügen' : 'Add file'),
    uploading:
      attachmentsDict?.uploading ?? (isDe ? 'Wird hochgeladen…' : 'Uploading…'),
    businessCard:
      attachmentsDict?.businessCard ??
      (isDe ? 'Visitenkarte' : 'Business card'),
    image: attachmentsDict?.image ?? (isDe ? 'Bild' : 'Image'),
    document: attachmentsDict?.document ?? (isDe ? 'Dokument' : 'Document'),
    uploadFailed:
      attachmentsDict?.uploadFailed ??
      (isDe
        ? 'Datei konnte nicht hochgeladen werden'
        : 'Failed to upload file'),
    removeFailed:
      attachmentsDict?.removeFailed ??
      (isDe
        ? 'Anhang konnte nicht entfernt werden'
        : 'Failed to remove attachment'),
    removed:
      attachmentsDict?.removed ??
      (isDe ? 'Anhang entfernt' : 'Attachment removed'),
    added:
      attachmentsDict?.added ??
      (isDe ? 'Anhang hinzugefügt' : 'Attachment added'),
    fileTooLarge:
      attachmentsDict?.fileTooLarge ??
      (isDe
        ? '"{name}" überschreitet die maximale Dateigröße.'
        : '"{name}" exceeds the maximum file size.'),
    fileTypeNotAllowed:
      attachmentsDict?.fileTypeNotAllowed ??
      (isDe
        ? 'Dateityp nicht erlaubt: "{name}"'
        : 'File type not allowed: "{name}"'),
    open: attachmentsDict?.open ?? (isDe ? 'Datei öffnen' : 'Open file'),
    remove:
      attachmentsDict?.remove ??
      (isDe ? 'Anhang entfernen' : 'Remove attachment'),
    removeConfirmTitle:
      attachmentsDict?.removeConfirmTitle ??
      (isDe ? 'Anhang entfernen?' : 'Remove attachment?'),
    removeConfirmDescriptionPrefix:
      attachmentsDict?.removeConfirmDescriptionPrefix ??
      (isDe ? 'Möchtest du ' : 'Are you sure you want to remove '),
    removeConfirmDescriptionSuffix:
      attachmentsDict?.removeConfirmDescriptionSuffix ??
      (isDe
        ? ' wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
        : '? This action cannot be undone.'),
    removeConfirmDescriptionFallback:
      attachmentsDict?.removeConfirmDescriptionFallback ??
      (isDe
        ? 'Möchtest du diesen Anhang wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
        : 'Are you sure you want to remove this attachment? This action cannot be undone.'),
    confirmRemove:
      attachmentsDict?.confirmRemove ?? (isDe ? 'Entfernen' : 'Remove'),
    cancel: detailDict.cancel ?? (isDe ? 'Abbrechen' : 'Cancel'),
    reorder:
      attachmentsDict?.reorder ?? (isDe ? 'Reihenfolge ändern' : 'Reorder'),
    reorderFailed:
      attachmentsDict?.reorderFailed ??
      (isDe
        ? 'Reihenfolge konnte nicht gespeichert werden'
        : 'Failed to save order'),
  };
}

export function buildLeadDetailPropertyLabels(options: {
  detailDict: {
    properties: string;
    status: string;
    jobTitle: string;
    copyField: string;
  };
  createDict: LeadCreateDict;
}) {
  const { detailDict, createDict } = options;
  return {
    properties: detailDict.properties,
    status: detailDict.status,
    email: createDict.email,
    phone: createDict.phone,
    company: createDict.workspace,
    jobTitle: detailDict.jobTitle,
    address: createDict.address,
    copyField: detailDict.copyField,
  };
}
